import { SessionCode } from "@hali-shares/deck-schema";
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import { usePhoneSession } from "../../live/usePhoneSession";
import { PhoneLive } from "./PhoneLive";
import "./phone.css";
import { APP_NAME } from "../../lib/brand";

type Phase = "checking" | "join" | "live";

/** /j and /j/:code. Checks whether this phone already joined (reload keeps its votes), else shows Join. */
export function JoinPage() {
  const { code: raw } = useParams();
  const parsed = raw ? SessionCode.safeParse(raw) : null;
  const code = parsed?.success ? parsed.data : null;
  const [phase, setPhase] = useState<Phase>(code ? "checking" : "join");
  const [error, setError] = useState<string | null>(raw && !code ? "That code doesn't look right." : null);
  const [attempt, setAttempt] = useState(0);
  const live = usePhoneSession(phase === "live" ? code : null, attempt);
  // The server forgot this phone (cookie cleared): back to Join.
  const shown: Phase = phase === "live" && live.needsJoin ? "join" : phase;

  useEffect(() => {
    if (!code) return;
    let alive = true;
    api.sessionMe(code).then(
      (me) => alive && setPhase(me.joined ? "live" : "join"),
      (e: unknown) => {
        if (!alive) return;
        setPhase("join");
        setError(
          e instanceof ApiError && e.status === 404 ? "No session with that code." : "Could not reach the session.",
        );
      },
    );
    return () => {
      alive = false;
    };
  }, [code]);

  return (
    <div className="phone">
      <header className="phone-head">
        <span className="phone-wordmark">{APP_NAME}</span>
        {code && <span className="phone-code">{code}</span>}
      </header>
      {shown === "join" && (
        <JoinScreen
          key={error ?? ""}
          initial={code ?? ""}
          error={error}
          onJoined={() => {
            setError(null);
            setAttempt((a) => a + 1);
            setPhase("live");
          }}
        />
      )}
      {shown === "live" && code && <PhoneLive code={code} live={live} />}
    </div>
  );
}

function JoinScreen({
  initial,
  error: initialError,
  onJoined,
}: {
  initial: string;
  error: string | null;
  onJoined: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState(initialError);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = SessionCode.safeParse(value);
    if (!parsed.success) return setError("Codes are 4 letters or digits.");
    setBusy(true);
    setError(null);
    try {
      await api.joinSession(parsed.data);
      if (parsed.data !== initial) navigate(`/j/${parsed.data}`, { replace: true });
      onJoined();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404 ? "No session with that code." : "Could not join. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="phone-body" onSubmit={submit}>
      <h1 className="phone-h">Join the session</h1>
      <p className="phone-text">
        {initial ? "You scanned the QR code, so the code is already filled in." : "Enter the code on the screen."}
      </p>
      <div className="phone-field">
        <label htmlFor="code">Session code</label>
        <input
          id="code"
          className="phone-input"
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          maxLength={4}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
        />
      </div>
      {error && (
        <p className="phone-error" role="alert">
          {error}
        </p>
      )}
      <button className="phone-btn" type="submit" disabled={busy}>
        Join
      </button>
      <span className="phone-foot">No sign-in. Votes are anonymous.</span>
    </form>
  );
}
